import styled, { keyframes } from "styled-components";

const shimmer = keyframes`
	0% { background-position: -240px 0; }
	100% { background-position: 240px 0; }
`;

export const SkeletonRow = styled.div`
	height: 66px;
	width: 100%;
	border-radius: 12px;
	background: linear-gradient(90deg, #f3f5f8 25%, #e4e8ef 50%, #f3f5f8 75%);
	background-size: 200% 100%;
	animation: ${shimmer} 1.5s infinite linear;
`;

export const ModalPaper = styled.div`
	background-color: #ffffff;
	border: 0;
	border-radius: 18px;
	width: min(540px, calc(100% - 24px));
	height: min(600px, 90vh);
	box-shadow: 0 22px 70px rgba(0, 14, 75, 0.3);
	overflow: hidden;
	display: flex;
	flex-direction: column;
	outline: none;
	position: relative;
`;

export const ModalTop = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 18px;
	align-items: center;
	padding: 21px 22px;
	border-bottom: 1px solid #edf0f4;
`;

export const ModalTitleWrap = styled.div`
	display: flex;
	align-items: center;
	gap: 13px;
`;

export const ModalIcon = styled.span`
	width: 18px;
	height: 18px;
	display: grid;
	place-items: center;
	color: #14246b;

	svg {
		width: 18px;
		height: 18px;
		display: block;
	}
`;

export const ModalTitle = styled.h2`
	font-family: "NotoSans-Bold", sans-serif;
	font-size: 18px;
	font-weight: 700;
	margin: 0;
	color: #3d4259;
`;

export const CloseButton = styled.button`
	border: 0;
	background: transparent;
	width: 32px;
	height: 32px;
	font-size: 24px;
	line-height: 1;
	color: #000e4b;
	display: grid;
	place-items: center;
	border-radius: 50%;
	cursor: pointer;
	transition: background-color 0.2s ease;
`;

export const ModalDescription = styled.div`
	padding: 20px 22px 11px;

	p {
		color: #797d8d;
		font-family: "NotoSans-Regular", sans-serif;
		font-size: 12px;
		line-height: 1.5;
		margin: 0;
	}
`;

export const ModalContent = styled.div`
	padding: 0 22px 22px;
	overflow-y: auto;
	flex: 1;
	display: flex;
	flex-direction: column;

	/* Chrome, Safari e Edge */
	&::-webkit-scrollbar {
		width: 6px;
	}
	&::-webkit-scrollbar-thumb {
		background-color: #e4e7ed;
		border-radius: 10px;
	}
	&::-webkit-scrollbar-track {
		background: transparent;
	}

	/* Firefox */
	scrollbar-width: thin;
	scrollbar-color: #e4e7ed transparent;
`;

export const PatientSearch = styled.div`
	height: 44px;
	border-bottom: 1px solid #e4e7ed;
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 16px;

	svg {
		width: 18px;
		height: 18px;
		color: #000e4b;
	}

	input {
		border: 0;
		outline: 0;
		width: 100%;
		color: #000e4b;
		font-family: "NotoSans-Regular", sans-serif;
		font-size: 13px;
		background: transparent;

		&::placeholder {
			color: #a3abc0;
		}
	}
`;

export const PatientList = styled.div`
	display: grid;
	gap: 10px;
`;

export const PatientItem = styled.article`
	border: 1px solid #e2e5eb;
	border-radius: 12px;
	background: #ffffff;
	padding: 11px 12px;
	transition: border-color 0.2s ease;
`;

export const PatientSummary = styled.div`
	display: grid;
	grid-template-columns: auto 1fr auto;
	align-items: center;
	gap: 12px;
`;

export const ListAvatar = styled.span<{ hasPhoto?: boolean }>`
	width: 42px;
	height: 42px;
	border-radius: 50%;
	display: grid;
	place-items: center;
	font-family: "NotoSans-Bold", sans-serif;
	color: #ffffff;
	font-size: 11px;
	font-weight: 800;
	border: 1px solid #cfd5df;
	background: #000e4b;
	overflow: hidden;
`;

export const ListPhoto = styled.img`
	width: 100%;
	height: 100%;
	border-radius: 50%;
	object-fit: cover;
`;

export const PatientInfo = styled.div`
	display: flex;
	flex-direction: column;
`;

export const ListName = styled.div`
	font-family: "Inter-Bold", sans-serif;
	font-size: 13px;
	font-weight: 700;
	color: #111827;
	margin-bottom: 4px;
`;

export const ListMeta = styled.div`
	font-family: "Inter-Regular", sans-serif;
	font-size: 9px;
	color: #6b7280;
`;

export const SendReportButton = styled.button`
	border-radius: 7px;
	padding: 9px 14px;
	min-width: 116px;
	height: 35px;
	font-size: 11px;
	font-weight: 700;
	font-family: "Inter-SemiBold", sans-serif;
	cursor: pointer;
	border: 1px solid #dedfe3;
	background: #ffffff;
	color: #111827;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition: background-color 0.2s ease, border-color 0.2s ease,
		color 0.2s ease;

	&:hover:not(:disabled) {
		border-color: #00a377;
		color: #00a377;
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.85;
	}
`;

export const LoadMoreContainer = styled.div`
	width: 100%;
	cursor: pointer;
	border-radius: 12px;
	margin-top: 20px;
	align-items: center;
	justify-content: center;
	display: flex;
	padding: 10px;
`;

export const LoadMoreButton = styled.button`
	display: block;
	margin: 26px auto 4px;
	border: 0;
	border-bottom: 1px solid currentColor;
	background: transparent;
	color: #007c68;
	font-family: "NotoSans-Bold", sans-serif;
	font-size: 11px;
	font-weight: 700;
	padding: 0;
	cursor: pointer;
	transition: color 0.2s ease;

	&:hover {
		color: #005a48;
	}
`;

export const NoResults = styled.div`
	font-family: "NotoSans-Regular", sans-serif;
	text-align: center;
	padding: 28px 10px;
	color: #797d8d;
	font-size: 13px;
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
`;
